import { HandlingUnitDto, StockItemDto, StorageLocationDto } from '@nest/domain-entities-metadata-generated';
import { Button, Label, Numeric, Tabs, TabsContent, TabsList, TabsTrigger, useValidationResultDialog } from '@nest/uikit-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useTerminalClient } from '../client-rpc-router';
import { HandlingUnitScanInput } from '../components/HandlingUnitScanInput';
import { StockItemLinkDetail } from '../components/StockItemLinkDetail';
import { StockItemScanInput } from '../components/StockItemScanInput';
import { StorageLocationScanInput } from '../components/StorageLocationScanInput';
import { TerminalScreen } from '../components/TerminalScreen';

export default function MoveStockItemPage() {
	const client = useTerminalClient();
	const navigate = useNavigate();

	const [activeTab, setActiveTab] = useState<'source' | 'items' | 'loaded'>('source');
	const [sourceSubTab, setSourceSubTab] = useState<'palette' | 'location'>('palette');
	const [sourceHandlingUnit, setSourceHandlingUnit] = useState<HandlingUnitDto | null>(null);
	const [sourceLocation, setSourceLocation] = useState<StorageLocationDto | null>(null);
	const [destinationLocation, setDestinationLocation] = useState<StorageLocationDto | null>(null);
	const [destinationHandlingUnit, setDestinationHandlingUnit] = useState<HandlingUnitDto | null>(null);
	const [destinationSubTab, setDestinationSubTab] = useState<'palette' | 'location'>('palette');
	const [quantity, setQuantity] = useState<number | null>(null);
	const [stockItem, setStockItem] = useState<StockItemDto | null>(null);
	const [bottomElementRef, setBottomElementRef] = useState<HTMLDivElement | null>(null);
	const quantityInputRef = useRef<HTMLInputElement | null>(null);

	const loadedItemsQuery = useQuery({
		queryKey: ['warehouse-move-loaded-items'],
		queryFn: () => client.warehouseMove.getMoveLoadedItems.execute({})
	});

	const itemsLoadedCount = loadedItemsQuery.data?.length ?? 0;

	const onSourceHandlingUnitChange = useCallback((value: HandlingUnitDto | null) => {
		setSourceHandlingUnit(value);
		setActiveTab('items');
	}, []);

	const onSourceLocationChange = useCallback((value: StorageLocationDto | null) => {
		setSourceLocation(value);
		setActiveTab('items');
	}, []);

	const handleQuantityChange = useCallback((value: number | null) => {
		setQuantity(value);
	}, []);

	const loadMutation = useMutation({
		mutationFn: async () => {
			await client.warehouseMove.processMoveLoadMutation.execute({
				type: 'Sku',
				items: [
					{
						stockItemId: stockItem?.id,
						sourcePaletteId: sourceHandlingUnit?.id ?? undefined,
						sourceWarehouseLocationId: sourceLocation?.id ?? undefined,
						quantity
					}
				]
			});
		},
		onSuccess: () => {
			setStockItem(null);
			setQuantity(null);
			setActiveTab('loaded');
			loadedItemsQuery.refetch();
		}
	});

	const unloadMutation = useMutation({
		mutationFn: async () => {
			await client.warehouseMove.processMoveUnloadMutation.execute({
				type: 'Sku',
				destinationLocationId: destinationSubTab === 'location' ? (destinationLocation?.id ?? undefined) : undefined,
				destinationPaletteId: destinationSubTab === 'palette' ? (destinationHandlingUnit?.id ?? undefined) : undefined
			});
		},
		onSuccess: () => {
			toast.success('Položky byly vyloženy');
			navigate('/move');
		}
	});

	useValidationResultDialog([loadMutation.error, unloadMutation.error]);

	const handleStockItemScanned = useCallback((value: StockItemDto | null) => {
		setStockItem(value);

		if (value != null) {
			setActiveTab('items');
		}
	}, []);

	useEffect(() => {
		if (activeTab === 'items' && stockItem != null) {
			quantityInputRef.current?.focus();
		}
	}, [activeTab, stockItem]);

	return (
		<TerminalScreen showBackButton backButtonTo="/move" topBar={<div>Přesun zboží</div>} bottomContent={<div ref={setBottomElementRef} />}>
			<div className="p-2 flex flex-col gap-4">
				<Tabs value={activeTab} onValueChange={v => setActiveTab(v as 'source' | 'items' | 'loaded')} className="flex-1">
					<TabsContent value="source" className="space-y-3">
						<Tabs value={sourceSubTab} onValueChange={v => setSourceSubTab(v as 'palette' | 'location')} className="w-full">
							<TabsList className="grid grid-cols-2 w-full">
								<TabsTrigger value="palette">Paleta</TabsTrigger>
								<TabsTrigger value="location">Lokace</TabsTrigger>
							</TabsList>
							<TabsContent value="palette" className="pt-3">
								<HandlingUnitScanInput placeholder="Naskenujte klec..." value={sourceHandlingUnit} onChange={onSourceHandlingUnitChange} autoFocus />
							</TabsContent>
							<TabsContent value="location" className="pt-3">
								<StorageLocationScanInput placeholder="Naskenujte lokaci..." value={sourceLocation} onChange={onSourceLocationChange} autoFocus />
							</TabsContent>
						</Tabs>
					</TabsContent>

					<TabsContent value="items" className="space-y-3">
						<Label className="mb-1 block">Položka</Label>
						<StockItemScanInput value={stockItem} onChange={handleStockItemScanned} autoFocus />

						<div>
							<Label className="mb-1 block">Množství</Label>
							<Numeric ref={quantityInputRef} value={quantity} onChange={handleQuantityChange} />
						</div>

						<Button onClick={() => loadMutation.mutate()} disabled={loadMutation.isPending} className="w-full mt-2">
							Naložit
						</Button>
					</TabsContent>

					<TabsContent value="loaded" className="space-y-5">
						<Label className="mb-1 block">Cílové umístění</Label>
						<Tabs value={destinationSubTab} onValueChange={v => setDestinationSubTab(v as 'palette' | 'location')} className="w-full">
							<TabsList className="grid grid-cols-2 w-full">
								<TabsTrigger value="palette">Paleta</TabsTrigger>
								<TabsTrigger value="location">Lokace</TabsTrigger>
							</TabsList>
							<TabsContent value="palette" className="pt-3">
								<HandlingUnitScanInput placeholder="Naskenujte klec..." value={destinationHandlingUnit} onChange={setDestinationHandlingUnit} autoFocus />
							</TabsContent>
							<TabsContent value="location" className="pt-3">
								<StorageLocationScanInput placeholder="Naskenujte lokaci..." value={destinationLocation} onChange={setDestinationLocation} autoFocus />
							</TabsContent>
						</Tabs>

						<Label className="mb-1 block">Naložené položky</Label>
						<div className="mt-2 space-y-2 divide-y gap-2 p-2 rounded-md border border-input-border">
							{loadedItemsQuery.data?.map(item => (
								<div key={`${item.stockItem?.id}-${item.handlingUnit?.id ?? 'nolp'}`} className="flex items-center gap-3 py-1">
									<div className="rounded-md bg-slate-100 px-2 py-1 text-sm font-semibold">{item.quantity}ks</div>
									<StockItemLinkDetail stockItemId={item.stockItem?.id} name={item.stockItem?.item?.name} />
								</div>
							))}

							{loadedItemsQuery.data?.length === 0 && <div className="text-sm text-muted-foreground">Žádné položky nebyly naloženy</div>}
						</div>

						<div className="mt-2 sticky bottom-0 bg-background">
							<Button
								onClick={() => unloadMutation.mutate()}
								className="w-full"
								disabled={unloadMutation.isPending || (destinationSubTab === 'location' ? !destinationLocation : !destinationHandlingUnit)}
							>
								Vyložit
							</Button>
						</div>
					</TabsContent>

					{bottomElementRef &&
						createPortal(
							<TabsList className="grid grid-cols-3 w-full">
								<TabsTrigger value="source" className="w-full">
									Zdroj
								</TabsTrigger>
								<TabsTrigger value="items" className="w-full">
									Položky
								</TabsTrigger>
								<TabsTrigger value="loaded" className="w-full">
									Naložené ({itemsLoadedCount})
								</TabsTrigger>
							</TabsList>,
							bottomElementRef
						)}
				</Tabs>
			</div>
		</TerminalScreen>
	);
}
